import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api.config";

export const soiContactApi = createApi({
  reducerPath: "soiContactApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["SOIContact", "SOIStats"],
  endpoints: (builder) => ({
    // Get all contacts
    getAllSOIContacts: builder.query({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params.status) queryParams.append("status", params.status);
        if (params.relationship) queryParams.append("relationship", params.relationship);
        if (params.search) queryParams.append("search", params.search);
        if (params.priority) queryParams.append("priority", params.priority);
        if (params.needsAttention) queryParams.append("needsAttention", params.needsAttention);
        if (params.sortBy) queryParams.append("sortBy", params.sortBy);
        if (params.sortOrder) queryParams.append("sortOrder", params.sortOrder);
        if (params.limit) queryParams.append("limit", params.limit);
        return `/soi-contacts?${queryParams.toString()}`;
      },
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ _id }) => ({ type: "SOIContact", id: _id })),
              { type: "SOIContact", id: "LIST" },
            ]
          : [{ type: "SOIContact", id: "LIST" }],
    }),

    // Get single contact
    getSOIContactById: builder.query({
      query: (id) => `/soi-contacts/${id}`,
      providesTags: (result, error, id) => [{ type: "SOIContact", id }],
    }),

    // Create contact
    createSOIContact: builder.mutation({
      query: (contactData) => ({
        url: "/soi-contacts",
        method: "POST",
        body: contactData,
      }),
      invalidatesTags: [
        { type: "SOIContact", id: "LIST" },
        { type: "SOIStats" },
      ],
    }),

    // Update contact
    updateSOIContact: builder.mutation({
      query: ({ id, ...contactData }) => ({
        url: `/soi-contacts/${id}`,
        method: "PUT",
        body: contactData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "SOIContact", id },
        { type: "SOIContact", id: "LIST" },
        { type: "SOIStats" },
      ],
    }),

    // Delete contact
    deleteSOIContact: builder.mutation({
      query: (id) => ({
        url: `/soi-contacts/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "SOIContact", id: "LIST" },
        { type: "SOIStats" },
      ],
    }),

    // Archive contact
    archiveSOIContact: builder.mutation({
      query: (id) => ({
        url: `/soi-contacts/${id}/archive`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "SOIContact", id: "LIST" },
        { type: "SOIStats" },
      ],
    }),

    // Log touch
    logTouch: builder.mutation({
      query: ({ id, ...touchData }) => ({
        url: `/soi-contacts/${id}/touch`,
        method: "POST",
        body: touchData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "SOIContact", id },
        { type: "SOIContact", id: "LIST" },
        { type: "SOIStats" },
      ],
    }),

    // Log referral
    logReferral: builder.mutation({
      query: ({ id, ...referralData }) => ({
        url: `/soi-contacts/${id}/referral`,
        method: "POST",
        body: referralData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "SOIContact", id },
        { type: "SOIContact", id: "LIST" },
        { type: "SOIStats" },
      ],
    }),

    // Update referral
    updateReferral: builder.mutation({
      query: ({ contactId, referralId, ...updateData }) => ({
        url: `/soi-contacts/${contactId}/referral/${referralId}`,
        method: "PUT",
        body: updateData,
      }),
      invalidatesTags: (result, error, { contactId }) => [
        { type: "SOIContact", id: contactId },
      ],
    }),

    // Get stats
    getSOIStats: builder.query({
      query: () => "/soi-contacts/stats",
      providesTags: [{ type: "SOIStats" }],
    }),

    // Get contacts needing attention
    getContactsNeedingAttention: builder.query({
      query: (limit = 10) => `/soi-contacts/needs-attention?limit=${limit}`,
    }),

    // Get upcoming dates
    getUpcomingDates: builder.query({
      query: (days = 30) => `/soi-contacts/upcoming-dates?days=${days}`,
    }),

    // Bulk log touch
    bulkLogTouch: builder.mutation({
      query: ({ contactIds, touchData }) => ({
        url: "/soi-contacts/bulk-touch",
        method: "POST",
        body: { contactIds, touchData },
      }),
      invalidatesTags: [
        { type: "SOIContact", id: "LIST" },
        { type: "SOIStats" },
      ],
    }),

    // Import from client
    importFromClient: builder.mutation({
      query: (clientData) => ({
        url: "/soi-contacts/import-client",
        method: "POST",
        body: clientData,
      }),
      invalidatesTags: [
        { type: "SOIContact", id: "LIST" },
        { type: "SOIStats" },
      ],
    }),
  }),
});

export const {
  useGetAllSOIContactsQuery,
  useGetSOIContactByIdQuery,
  useCreateSOIContactMutation,
  useUpdateSOIContactMutation,
  useDeleteSOIContactMutation,
  useArchiveSOIContactMutation,
  useLogTouchMutation,
  useLogReferralMutation,
  useUpdateReferralMutation,
  useGetSOIStatsQuery,
  useGetContactsNeedingAttentionQuery,
  useGetUpcomingDatesQuery,
  useBulkLogTouchMutation,
  useImportFromClientMutation,
} = soiContactApi;
